// src/features/analytics/searchParams.ts
//
// Typed URL search params for the /analytics layout route (ADR-003 §3).
// Plain validation function — no Zod (forms-only per CLAUDE.md). URL state
// makes views shareable and refresh-stable without a new context/store.

import {
  MAX_RANGE_DAYS,
  SECONDS_PER_DAY,
  clampRangeSpan,
  presetToRange,
  type UtcRange,
} from '@/shared/utils/utcRange';
import {
  ANALYTICS_DIMENSIONS,
  type AnalyticsDimension,
  type AnalyticsScopeKind,
} from '@/lib/constants/analytics';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';

/** Presets offered by the New Analytics range control. */
export const ANALYTICS_RANGE_PRESETS = [
  'today',
  'last7d',
  'last30d',
  'last90d',
  'ytd',
  'priorYear',
  'custom',
] as const;
export type AnalyticsRangePreset = typeof ANALYTICS_RANGE_PRESETS[number];

export const ANALYTICS_DEFAULT_PRESET = 'last30d' as const;

/** Preset display labels (range control + the map header's range wording). */
export const ANALYTICS_PRESET_LABELS: Record<AnalyticsRangePreset, string> = {
  today: 'Today',
  last7d: 'Last 7 days',
  last30d: 'Last 30 days',
  last90d: 'Last 90 days',
  ytd: 'Year to date',
  priorYear: 'Prior year',
  custom: 'Custom',
};

export interface AnalyticsSearch {
  scope: AnalyticsScopeKind;
  preset: AnalyticsRangePreset;
  /** UTC epoch seconds; present only when preset === 'custom'. */
  from?: number;
  /** UTC epoch seconds (end-exclusive); present only when preset === 'custom'. */
  to?: number;
  /**
   * Audience-tab dimension (plan §4.4: one URL param under the two-group
   * control so links stay shareable). Absent means the default (country);
   * other tabs ignore it.
   */
  dimension?: AnalyticsDimension;
  /**
   * Audience-tab geo presentation (plan §4.1 List | Map toggle, Seq U-6).
   * Absent means the default list; only 'map' is ever stored, mirroring the
   * dimension param's absent-default pattern. Ignored (list renders) while a
   * technology dimension is active, but preserved in the URL so flipping
   * back to a geo dimension restores the map.
   */
  view?: 'map';
}

function asEpochSeconds(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isInteger(n) || n <= 0) return undefined;
  return n;
}

/**
 * validateSearch for the /analytics layout route. Invalid input degrades to
 * defaults. `expansionEnabled` is injectable for deterministic tests only —
 * the router always uses the build-time flag (a shared URL cannot expose the
 * dark embedDomain dimension while the expansion ships dark).
 */
export function validateAnalyticsSearch(
  search: Record<string, unknown>,
  expansionEnabled: boolean = FEATURE_FLAGS.analyticsExpansionEnabled
): AnalyticsSearch {
  const scope: AnalyticsScopeKind = search.scope === 'network' ? 'network' : 'account';

  let preset: AnalyticsRangePreset = ANALYTICS_RANGE_PRESETS.includes(
    search.preset as AnalyticsRangePreset
  )
    ? (search.preset as AnalyticsRangePreset)
    : ANALYTICS_DEFAULT_PRESET;

  let from: number | undefined;
  let to: number | undefined;
  if (preset === 'custom') {
    from = asEpochSeconds(search.from);
    to = asEpochSeconds(search.to);
    if (from === undefined || to === undefined || from >= to) {
      preset = ANALYTICS_DEFAULT_PRESET;
      from = undefined;
      to = undefined;
    }
  }

  const dimension =
    ANALYTICS_DIMENSIONS.includes(search.dimension as AnalyticsDimension) &&
    (expansionEnabled || search.dimension !== 'embedDomain')
      ? (search.dimension as AnalyticsDimension)
      : undefined;

  const view = search.view === 'map' ? 'map' : undefined;

  return { scope, preset, from, to, dimension, view };
}

/**
 * Resolve the validated search to a concrete end-exclusive UTC range, applying
 * the product-window floor and the 1095-day gateway cap (custom inputs from a
 * stale/shared URL could exceed either; the picker prevents new ones).
 */
export function resolveAnalyticsRange(
  search: AnalyticsSearch,
  nowSeconds: number,
  floorSeconds: number | null
): UtcRange {
  if (search.preset === 'custom' && search.from !== undefined && search.to !== undefined) {
    let range = clampRangeSpan({ from: search.from, to: search.to }, MAX_RANGE_DAYS);
    if (floorSeconds !== null && range.from < floorSeconds) {
      range = {
        from: Math.min(floorSeconds, range.to - SECONDS_PER_DAY),
        to: range.to,
      };
    }
    return range;
  }
  const preset = search.preset === 'custom' ? ANALYTICS_DEFAULT_PRESET : search.preset;
  return presetToRange(preset, nowSeconds, floorSeconds ?? undefined);
}

/**
 * The span the customer actually SELECTED — the preset's natural range with
 * NO product-window floor (only the span cap, matching resolveAnalyticsRange's
 * custom handling). The coverage notice compares data against this so a
 * silently floor-clamped preset ("Last 90 days" fetching Jul 8 onward) still
 * tells the customer what their selection reached back to; queries keep using
 * the floored resolveAnalyticsRange result (the gateway rejects pre-window
 * ranges).
 */
export function resolveSelectedAnalyticsRange(
  search: AnalyticsSearch,
  nowSeconds: number
): UtcRange {
  if (search.preset === 'custom' && search.from !== undefined && search.to !== undefined) {
    return clampRangeSpan({ from: search.from, to: search.to }, MAX_RANGE_DAYS);
  }
  const preset = search.preset === 'custom' ? ANALYTICS_DEFAULT_PRESET : search.preset;
  return presetToRange(preset, nowSeconds);
}
